import {useEffect, useRef} from 'react';
import {signOut} from 'aws-amplify/auth';
import {Center} from '@mantine/core';
import {PlatformLoader} from '@flexinet/ui-components';
import {config} from '../../../config/amplify/auth';

// Target of the user menu's "Sign Out" entry (UserLayout). Clears the Amplify
// session, then leaves for the portal through the broker's /logout.
//
// ⚠️ A bare signOut() REJECTS on every deployed stage (FNP-1674): Amplify
// clears the tokens, then refuses the logout redirect because
// `redirectSignOut` is the portal, a different origin (InvalidOriginException).
// Naming this stage's portal as `redirectUrl` (FNP-1685) makes Amplify go via
// the broker's /logout to the portal. Amplify starts that navigation and
// resolves, so NO `finally` redirect: it would override the navigation. When
// there is no OAuth session, signOut() resolves without navigating, so the
// timer is the fallback. A slow broker /logout can outlast the timer, so
// neither the timer nor the catch acts once the page has started to unload
// (D14c). StrictMode runs this effect twice in dev, and a second signOut()
// rejecting would cancel the first one's navigation, so it runs once (D14d).
const FALLBACK_REDIRECT_MS = 3000;

export const SignOutPage = () => {
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        const signOutAndRedirect = async () => {
            window.localStorage.setItem('amplify-signin-with-hostedUI', 'false');
            const redirectUrl = config?.Cognito.loginWith?.oauth?.redirectSignOut[0];
            let leaving = false;
            window.addEventListener('beforeunload', () => {
                leaving = true;
            });
            try {
                await signOut({global: false, oauth: {redirectUrl}});
                window.setTimeout(() => {
                    if (!leaving) window.location.href = '/';
                }, FALLBACK_REDIRECT_MS);
            } catch (error) {
                console.error('Sign out did not complete cleanly', error);
                if (!leaving) window.location.href = '/';
            }
        };

        signOutAndRedirect();
    }, []);

    return (
        <Center>
            <PlatformLoader message={'Signing out...'}/>
        </Center>
    );
};
